import { getAgent } from './agents';
import { euro, fromMinutes, toMinutes } from './time';
import type { AvatarThread } from '../types/anna';
import type { Proposal, Tables } from '../types/db';

export const traceById = (t: Tables, id: string) => t.traces.find((x) => x.id === id);

export const pendingProposals = (t: Tables) => t.proposals.filter((p) => p.status === 'wacht');

export function proposalLines(t: Tables, p: Proposal): string[] {
  if (p.kind === 'mail') {
    const draft = t.mailDrafts.find((d) => d.id === p.refIds[0]);
    return draft ? [`“${draft.body}”`] : [];
  }
  if (p.kind === 'code') {
    const pr = t.pullRequests.find((x) => x.id === p.refIds[0]);
    if (!pr) return [];
    const add = pr.files.reduce((s, f) => s + f.additions, 0);
    const del = pr.files.reduce((s, f) => s + f.deletions, 0);
    return [
      `${pr.files.length} bestanden · +${add} −${del}`,
      pr.tests.failed === 0 ? `Alle ${pr.tests.passed} tests geslaagd` : `${pr.tests.failed} tests gefaald`,
    ];
  }
  const rs = t.receipts.filter((r) => p.refIds.includes(r.id));
  const total = rs.reduce((s, r) => s + r.amount, 0);
  const vat = rs.reduce((s, r) => s + (r.amount - r.amount / (1 + r.vatRate)), 0);
  return [`${euro(total)} totaal · btw ${euro(vat)}`, `Gekoppeld aan ${rs.length} banktransacties`];
}

/** Structured content of a proposal for the large approval card. */
export function proposalDetail(t: Tables, p: Proposal) {
  if (p.kind === 'mail') {
    const draft = t.mailDrafts.find((d) => d.id === p.refIds[0]);
    const contact = draft ? t.contacts.find((c) => c.id === draft.to) : undefined;
    return { kind: 'mail' as const, to: contact?.name ?? '', subject: draft?.subject ?? '', body: draft?.body ?? '' };
  }
  if (p.kind === 'code') {
    const pr = t.pullRequests.find((x) => x.id === p.refIds[0]) ?? t.pullRequests[0];
    return { kind: 'code' as const, pr };
  }
  const receipts = t.receipts.filter((r) => p.refIds.includes(r.id));
  const total = receipts.reduce((s, r) => s + r.amount, 0);
  const vat = receipts.reduce((s, r) => s + (r.amount - r.amount / (1 + r.vatRate)), 0);
  return { kind: 'boekhouding' as const, receipts, total, vat };
}

export const agendaToday = (t: Tables) => [...t.calendar].sort((a, b) => toMinutes(a.start) - toMinutes(b.start));

export function nextEvent(t: Tables) {
  const now = toMinutes(t.meta.time);
  return agendaToday(t).find((e) => toMinutes(e.start) >= now);
}

export const nightSummary = (t: Tables) => t.log.filter((l) => l.night);

export const activeTasks = (t: Tables) => t.agentTasks.filter((a) => a.status !== 'klaar');

export const avatarThreads = (t: Tables): AvatarThread[] =>
  t.agentTasks.filter((a) => a.status === 'bezig').map((a) => ({ color: getAgent(a.agent).color, speed: a.pace }));

export function weatherNow(t: Tables) {
  const hour = Math.floor(toMinutes(t.meta.time) / 60) * 60;
  return t.weather.find((w) => toMinutes(w.time) === hour) ?? t.weather[0];
}

/** Precipitation for the next 2 hours in quarter-hour steps, plus a short note. */
export function rainNext2h(t: Tables): { quarters: number[]; note: string } {
  const now = toMinutes(t.meta.time);
  const quarters = Array.from({ length: 8 }, (_, i) => {
    const hour = (Math.floor((now + 15 * (i + 1)) / 60) % 24) * 60;
    return t.weather.find((w) => toMinutes(w.time) === hour)?.mm ?? 0;
  });
  const first = quarters.findIndex((q) => q > 0);
  const note =
    first < 0 ? 'Droog komende 2 uur' : `Regen vanaf ${fromMinutes(Math.floor((now + 15 * (first + 1)) / 60) * 60)}`;
  return { quarters, note };
}

export const contactById = (t: Tables, id: string) => t.contacts.find((c) => c.id === id);

export const mailsFrom = (t: Tables, contactId: string) => t.mails.filter((m) => m.contactId === contactId);

export const roomById = (t: Tables, id: string) => t.rooms.find((r) => r.id === id);

export const deviceById = (t: Tables, id: string) => t.devices.find((d) => d.id === id);

/** Departure = start − travel − 5 min buffer; arrival 5 min before start. */
export function tripFor(t: Tables, eventId: string) {
  const e = t.calendar.find((x) => x.id === eventId);
  if (!e || !e.travelMinutes) return undefined;
  const start = toMinutes(e.start);
  return {
    event: e,
    depart: fromMinutes(start - e.travelMinutes - 5),
    arrive: fromMinutes(start - 5),
    minutes: e.travelMinutes,
  };
}
